import React, { useState, useEffect, useRef, useMemo } from 'react';
import Box from '@mui/material/Box';
import { 
  IconButton, 
  Button, 
  Stack, 
  CircularProgress, 
  Alert, 
  List, 
  ListItemButton, 
  ListItemIcon, 
  ListItemText,
  Typography,
  Paper,
  Tooltip,
  Popover,
  Card,
  CardContent,
  Divider,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  SelectChangeEvent,
  Link as MuiLink,
  Snackbar,
  InputAdornment
} from '@mui/material';
import DirectionsBoatIcon from '@mui/icons-material/DirectionsBoat';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import DashboardIcon from '@mui/icons-material/Dashboard';
import SettingsIcon from '@mui/icons-material/Settings';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import InfoIcon from '@mui/icons-material/Info';
import PersonIcon from '@mui/icons-material/Person';
import HomeIcon from '@mui/icons-material/Home';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import MapIcon from '@mui/icons-material/Map';
import SearchIcon from '@mui/icons-material/Search';
import { DndContext, useDraggable } from '@dnd-kit/core';
import { getAllSlips, upsertSlip, deleteSlip, Slip, toRelativePosition, toAbsolutePosition } from '../../../firebase/services/slipService';
import { getMarinaSettings, updateMarinaSettings } from '../../../firebase/services/marinaSettingsService';
import { getCustomerById, searchVesselsByRegistration, CustomerVessel, Customer } from '../../../firebase/services/customerVesselService';
import { getContractsBySlip, Contract } from '../../../firebase/services/contractService';
import FullScreenLayout from '../../../layouts/FullScreenLayout';
import { Link, useNavigate } from 'react-router-dom';
import { Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { CSS } from '@dnd-kit/utilities';
import SaveIcon from '@mui/icons-material/Save';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { useAuth } from '../../../contexts/AuthContext';
import { format } from 'date-fns';
import DescriptionIcon from '@mui/icons-material/Description';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

// Define types and constants
interface ImageMetadata {
  id: string;
  storageUrl: string;
  category: string;
  isPublic: boolean;
  [key: string]: any;
}

// Add this type at the top with other interfaces
interface ImageCoordinate {
  x: number;
  y: number;
}

// Add this interface for the editable slip form data
interface SlipFormData {
  slipNumber: string;
  renterName: string;
  boatName: string;
  boatType: string;
  phoneNumber: string;
  leaseEndDate: string;
}

// Define an interface for GPS coordinates
interface GpsCoordinates {
  latitude: number;
  longitude: number;
  accuracy: number;
}

// Define an interface for marina GPS boundaries
interface MarinaGpsBounds {
  northEast: { latitude: number; longitude: number; };
  southWest: { latitude: number; longitude: number; };
}

// Add a helper function to calculate distance between two GPS points using the Haversine formula
const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
  const R = 6371e3; // Earth radius in meters
  const φ1 = lat1 * Math.PI/180; // φ, λ in radians
  const φ2 = lat2 * Math.PI/180;
  const Δφ = (lat2-lat1) * Math.PI/180;
  const Δλ = (lon2-lon1) * Math.PI/180;

  const a = Math.sin(Δφ/2) * Math.sin(Δφ/2) +
          Math.cos(φ1) * Math.cos(φ2) *
          Math.sin(Δλ/2) * Math.sin(Δλ/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));

  return R * c; // in meters
};

// Mock component for ImageManager
const ImageManager = ({ category, onSelect, showPublicOnly }: { 
  category: string; 
  onSelect: (image: ImageMetadata) => void; 
  showPublicOnly: boolean;
}) => {
  // Mock data for testing
  const dummyImages: ImageMetadata[] = [
    {
      id: '1',
      storageUrl: '/static/images/marina-aerial.png',
      category: 'MARINA',
      isPublic: true,
      filename: 'marina-aerial.png'
    },
    {
      id: '2',
      storageUrl: 'https://firebasestorage.googleapis.com/v0/b/fllboats.appspot.com/o/marina-images%2Fmarina-aerial.jpg?alt=media',
      category: 'MARINA',
      isPublic: true,
      filename: 'marina-aerial.jpg'
    }
  ];
  
  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        {dummyImages.map(image => (
          <Box 
            key={image.id}
            sx={{ 
              width: 150, 
              height: 100, 
              backgroundImage: `url(${image.storageUrl})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              cursor: 'pointer',
              border: '1px solid #ccc',
              borderRadius: 1
            }}
            onClick={() => onSelect(image)}
          />
        ))}
      </Box>
    </Box>
  );
};

// Default gradient background
const DEFAULT_BACKGROUND_GRADIENT = 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)';

// Add a helper function to extract slip number from the slip ID
const extractSlipNumber = (slipId: string): string => {
  // Check if the ID is in the format "slip-X" where X is the slip number
  const match = slipId.match(/(\d+)/);
  if (match && match[1]) {
    return match[1];
  }
  
  // For IDs that don't match the pattern, just return the last part after dash
  const parts = slipId.split('-');
  return parts[parts.length - 1];
};

// Update the SlipMarker component to make the markers half the size
function SlipMarker({ slip, onDelete, onSlipClick, containerSize, dragDistance, slipsLocked }: {
  slip: Slip;
  onDelete: () => void;
  onSlipClick: (slip: Slip) => void;
  containerSize: { width: number, height: number };
  dragDistance: number;
  slipsLocked: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: slip.id,
    data: { slip },
    disabled: slipsLocked // Disable dragging when slips are locked
  });

  // Apply transform only while dragging, not for positioning
  const style = {
    transform: transform ? CSS.Translate.toString(transform) : undefined,
    zIndex: isDragging ? 10 : 1,
    transition: isDragging ? 'none' : 'box-shadow 0.2s',
    cursor: slipsLocked ? 'not-allowed' : 'grab',
  };

  // Determine slip status based on customer, vessel, contract, and payment status
  const hasCustomer = !!slip.customerId;
  const hasVessel = !!slip.assignedVesselId;
  const hasContract = !!slip.contractId;
  const isPaid = slip.paymentStatus === 'paid';
  const isLaunched = slip.launchStatus === 'launched';
  
  // Set colors based on slip status
  let markerColor;
  if (!hasCustomer) {
    markerColor = '#212121'; // Black - unassigned
  } else if (!hasContract || !isPaid) {
    markerColor = '#d32f2f'; // Red - contract or payment not complete
  } else if (!isLaunched) {
    markerColor = '#f57c00'; // Orange - vessel not launched yet
  } else {
    markerColor = '#2e7d32'; // Green - everything complete and launched
  }
  
  // Get the slip number from the slip object
  const slipNumber = slip.slipNumber || '?';
  
  // Calculate font size based on the length of the slip number
  const getFontSize = () => {
    const length = slipNumber.length;
    if (length === 1) return '12px';
    if (length === 2) return '10px';
    if (length === 3) return '8px';
    return '7px'; // For 4+ characters
  };
  
  const [showDeleteButton, setShowDeleteButton] = useState(false);
  const [clickStartTime, setClickStartTime] = useState(0);
  
  // Function to handle delete with confirmation
  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    
    // Add confirmation dialog
    if (window.confirm(`Are you sure you want to delete slip ${slipNumber}?`)) {
      onDelete();
    }
  };

  // Improved click handling using mousedown/mouseup to detect real clicks
  const handleMouseDown = (e: React.MouseEvent) => {
    // Record when the mouse down happened
    setClickStartTime(Date.now());
  };

  const handleMouseUp = (e: React.MouseEvent) => {
    // Only treat as a click if it was a short press and minimal movement
    if (Date.now() - clickStartTime < 200 && dragDistance < 5) {
      e.stopPropagation();
      onSlipClick(slip);
    }
  };
  
  // Marker size reduced by half from 48px to 24px
  const markerSize = 24;
  
  return (
    <g 
      transform={`translate(${slip.x}, ${slip.y})`}
      style={{ pointerEvents: 'all' }}
      onMouseEnter={() => setShowDeleteButton(true)}
      onMouseLeave={() => setShowDeleteButton(false)}
    >
      {/* The number marker */}
      <foreignObject 
        width={markerSize} 
        height={markerSize} 
        x={-markerSize/2} 
        y={-markerSize/2}
        style={{ overflow: 'visible', pointerEvents: 'all' }}
      >
        {/* Draggable container */}
        <div
          ref={setNodeRef}
          style={{
            ...style,
            width: `${markerSize}px`,
            height: `${markerSize}px`,
            position: 'relative',
            pointerEvents: 'all',
          }}
          {...attributes}
          {...listeners}
        >
          {/* Click overlay - handle clicks separately from drag */}
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              zIndex: 15,
              cursor: 'pointer',
              opacity: 0.01, // Nearly invisible
            }}
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
          />
          
          {/* Visual marker */}
          <div
            style={{
              backgroundColor: markerColor,
              border: `2px solid ${markerColor}`,
              borderRadius: '50%',
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: isDragging ? '0px 5px 5px rgba(0,0,0,0.3)' : '0px 2px 2px rgba(0,0,0,0.1)',
              position: 'absolute',
              top: 0,
              left: 0,
              fontWeight: 'bold',
              fontSize: getFontSize(), // Dynamic font size based on text length
              textAlign: 'center',
              color: 'white',
              zIndex: 10,
              padding: '1px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              userSelect: 'none',
            }}
          >
            {slipNumber}
          </div>
        </div>
      </foreignObject>
      
      {/* Separate delete button element - adjust position for smaller marker */}
      {showDeleteButton && !slipsLocked && (
        <foreignObject 
          width="20" 
          height="20" 
          x="12" 
          y="-20"
          style={{ overflow: 'visible', pointerEvents: 'all' }}
        >
          <div 
            style={{
              width: '20px',
              height: '20px',
              position: 'relative',
            }}
          >
            <button
              onClick={handleDelete}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '20px',
                height: '20px',
                backgroundColor: '#f44336',
                color: 'white',
                border: 'none',
                borderRadius: '50%',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 50,
                boxShadow: '0px 2px 4px rgba(0,0,0,0.3)',
              }}
              aria-label={`Delete slip ${slipNumber}`}
            >
              <DeleteIcon style={{ fontSize: '12px' }} />
            </button>
          </div>
        </foreignObject>
      )}
    </g>
  );
}

// HTML-based marker component for better cross-browser compatibility
function HTMLSlipMarker({ slip, onDelete, onSlipClick, containerSize, dragDistance, slipsLocked, imageDimensions }: {
  slip: Slip;
  onDelete: () => void;
  onSlipClick: (slip: Slip) => void;
  containerSize: { width: number, height: number };
  dragDistance: number;
  slipsLocked: boolean;
  imageDimensions: {
    width: number;
    height: number;
    offsetX: number;
    offsetY: number;
    naturalWidth: number;
    naturalHeight: number;
  };
}) {
  const [showDeleteButton, setShowDeleteButton] = useState(false);
  const [clickStartTime, setClickStartTime] = useState(0);
  
  // Determine slip status based on customer, vessel, contract, and payment status
  const hasCustomer = !!slip.customerId;
  const hasVessel = !!slip.assignedVesselId;
  const hasContract = !!slip.contractId;
  const isPaid = slip.paymentStatus === 'paid';
  const isLaunched = slip.launchStatus === 'launched';
  
  // Set colors based on slip status
  let markerColor;
  if (!hasCustomer) {
    markerColor = '#212121'; // Black - unassigned
  } else if (!hasContract || !isPaid) {
    markerColor = '#d32f2f'; // Red - contract or payment not complete
  } else if (!isLaunched) {
    markerColor = '#f57c00'; // Orange - vessel not launched yet
  } else {
    markerColor = '#2e7d32'; // Green - everything complete and launched
  }
  
  // Get the slip number from the slip object
  const slipNumber = slip.slipNumber || '?';
  
  // Calculate font size based on the length of the slip number
  const getFontSize = () => {
    const length = slipNumber.length;
    if (length === 1) return '12px';
    if (length === 2) return '10px';
    if (length === 3) return '8px';
    return '7px'; // For 4+ characters
  };
  
  // Function to handle delete with confirmation
  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    
    console.log("Delete button clicked for slip:", slip.id);
    
    // Add confirmation dialog
    if (window.confirm(`Are you sure you want to delete slip ${slipNumber}?`)) {
      console.log(`Confirmed delete for slip ${slip.id}`);
      onDelete();
    }
  };

  // Improved click handling using mousedown/mouseup to detect real clicks
  const handleMouseDown = (e: React.MouseEvent) => {
    // Record when the mouse down happened
    setClickStartTime(Date.now());
  };

  const handleMouseUp = (e: React.MouseEvent) => {
    // Only treat as a click if it was a short press and minimal movement
    if (Date.now() - clickStartTime < 200 && dragDistance < 5) {
      e.stopPropagation();
      console.log("Mouse click detected on slip:", slip.id);
      onSlipClick(slip);
    }
  };
  
  // Marker size reduced by half from 48px to 24px
  const markerSize = 24;
  
  // Calculate position on the screen based on slip coordinates and image dimensions
  const getSlipPosition = () => {
    if (!imageDimensions.width || !imageDimensions.naturalWidth) {
      return { left: 0, top: 0 };
    }
    
    // Calculate position based on the image's natural dimensions and current width
    const scaleX = imageDimensions.width / Math.max(imageDimensions.naturalWidth, 1);
    const scaleY = scaleX; // Maintain aspect ratio
    
    // Calculate the vertical offset for the centered image
    // This is the space between the top of the container and the top of the image
    const scaledImageHeight = imageDimensions.naturalHeight * scaleY;
    const containerHeight = containerSize.height;
    const verticalOffset = (containerHeight - scaledImageHeight) / 2;
    
    // Apply the vertical offset to the slip position
    const finalLeft = slip.x * scaleX;
    const finalTop = (slip.y * scaleY) + verticalOffset;
    
    // Debug log for the first slip to check values
    if (slip.slipNumber === 'A-1' || slip.slipNumber === 'A1' || slip.slipNumber === '1') {
      console.log('Slip position calculation:', {
        slipNumber: slip.slipNumber,
        slipX: slip.x,
        slipY: slip.y,
        scaleX,
        scaleY,
        scaledImageHeight,
        containerHeight,
        verticalOffset,
        finalLeft,
        finalTop
      });
    }
    
    return { left: finalLeft, top: finalTop };
  };
  
  const position = getSlipPosition();
  
  return (
    <div
      style={{
        position: 'absolute',
        left: `${position.left - markerSize/2}px`,
        top: `${position.top - markerSize/2}px`,
        width: `${markerSize}px`,
        height: `${markerSize}px`,
        zIndex: 10,
        cursor: slipsLocked ? 'not-allowed' : 'grab',
        userSelect: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'visible',
      }}
      onMouseEnter={() => setShowDeleteButton(true)}
      onMouseLeave={() => setShowDeleteButton(false)}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
    >
      {/* Visual marker */}
      <div
        style={{
          backgroundColor: markerColor,
          border: `2px solid ${markerColor}`,
          borderRadius: '50%',
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0px 2px 2px rgba(0,0,0,0.1)',
          fontWeight: 'bold',
          fontSize: getFontSize(),
          textAlign: 'center',
          color: 'white',
          zIndex: 10,
          padding: '1px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {slipNumber}
      </div>
      
      {/* Delete button */}
      {showDeleteButton && !slipsLocked && (
        <div
          style={{
            position: 'absolute',
            top: `-20px`,
            right: `-10px`,
            width: '20px',
            height: '20px',
            zIndex: 50,
          }}
        >
          <button
            onClick={handleDelete}
            style={{
              width: '20px',
              height: '20px',
              backgroundColor: '#f44336',
              color: 'white',
              border: 'none',
              borderRadius: '50%',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 50,
              boxShadow: '0px 2px 4px rgba(0,0,0,0.3)',
              padding: 0,
            }}
            aria-label={`Delete slip ${slipNumber}`}
          >
            <DeleteIcon style={{ fontSize: '12px' }} />
          </button>
        </div>
      )}
    </div>
  );
}

// Helper to optimize image URL based on screen size
const getOptimizedImageUrl = (url: string, width?: number, height?: number): string => {
  // If it's a Firebase Storage URL, we can add parameters to request resized images
  if (url && url.includes('firebasestorage.googleapis.com') && width && height) {
    // Some cloud storage services allow image resizing via URL parameters
    // For example: &width=1200&height=800
    if (url.includes('?')) {
      return `${url}&width=${width}&height=${height}`;
    } else {
      return `${url}?width=${width}&height=${height}`;
    }
  }
  
  // For non-Firebase URLs, return as is
  return url;
};

// Helper function to calculate the image dimensions when using "contain" mode
const calculateContainedImageDimensions = (
  imageWidth: number,
  imageHeight: number,
  containerWidth: number,
  containerHeight: number
): { width: number; height: number; offsetX: number; offsetY: number } => {
  // Calculate the aspect ratio
  const imageRatio = imageWidth / imageHeight;
  const containerRatio = containerWidth / containerHeight;
  
  let width, height, offsetX, offsetY;
  
  if (containerRatio > imageRatio) {
    // Container is wider than image (relative to height)
    height = containerHeight;
    width = height * imageRatio;
    offsetX = (containerWidth - width) / 2;
    offsetY = 0;
  } else {
    // Container is taller than image (relative to width)
    width = containerWidth;
    height = width / imageRatio;
    offsetX = 0;
    offsetY = (containerHeight - height) / 2;
  }
  
  return { width, height, offsetX, offsetY };
};

// Fix convertToImageCoordinates function
const convertToImageCoordinates = (
  clientX: number,
  clientY: number,
  containerRect: DOMRect,
  imageDimensions: { 
    width: number; 
    height: number; 
    offsetX: number; 
    offsetY: number;
    naturalWidth: number;
    naturalHeight: number;
  }
): ImageCoordinate => {
  // Calculate position relative to the container
  const relativeX = clientX - containerRect.left - imageDimensions.offsetX;
  const relativeY = clientY - containerRect.top - imageDimensions.offsetY;

  // Convert to image coordinates - handle case where naturalWidth/Height might be 0
  const naturalWidth = imageDimensions.naturalWidth || 1;
  const naturalHeight = imageDimensions.naturalHeight || 1;
  
  return {
    x: (relativeX / imageDimensions.width) * naturalWidth,
    y: (relativeY / imageDimensions.height) * naturalHeight
  };
};

// Add this after imports
const theme = createTheme({
  components: {
    MuiModal: {
      styleOverrides: {
        root: {
          zIndex: 9999,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        root: {
          zIndex: 9999,
        },
        paper: {
          boxShadow: '0px 0px 30px rgba(0,0,0,0.5)',
        },
      },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          zIndex: 9998,
        },
      },
    },
  },
});

// Add this helper function to generate a random 10-digit ID
const generateRandomId = (): string => {
  // Generate a random number between 1000000000 and 9999999999 (10 digits)
  return Math.floor(1000000000 + Math.random() * 9000000000).toString();
};

// Add conversion between GPS coordinates and image coordinates
const convertGpsToImageCoordinates = (
  latitude: number, 
  longitude: number,
  marinaBounds: MarinaGpsBounds,
  imageWidth: number,
  imageHeight: number
): { x: number, y: number } => {
  // Calculate the percentage position within the bounds
  const latPercent = (marinaBounds.northEast.latitude - latitude) / 
                     (marinaBounds.northEast.latitude - marinaBounds.southWest.latitude);
  const lonPercent = (longitude - marinaBounds.southWest.longitude) / 
                     (marinaBounds.northEast.longitude - marinaBounds.southWest.longitude);
  
  // Convert to image coordinates
  const x = lonPercent * imageWidth;
  const y = latPercent * imageHeight;
  
  return { x, y };
};

// Add this helper function near the top with other helper functions
const capitalizeWords = (str: string): string => {
  return str.split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const formatPhoneNumber = (phone: string): string => {
  if (!phone) return 'N/A';
  const cleaned = phone.replace(/\D/g, '');
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    return `(${match[1]}) ${match[2]}-${match[3]}`;
  }
  return phone;
};

const SlipManagementPage = (): React.ReactElement => {
  const { userProfile } = useAuth();
  const [slips, setSlips] = useState<Slip[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [marinaImage, setMarinaImage] = useState<string>('');
  const [imageError, setImageError] = useState(false);
  const [openImageSelector, setOpenImageSelector] = useState(false);
  const [settings, setSettings] = useState<any>(null);
  const [selectedSlip, setSelectedSlip] = useState<Slip | null>(null);
  const [slipDetailsAnchorEl, setSlipDetailsAnchorEl] = useState<null | HTMLElement>(null);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight
  });
  const [imageDimensions, setImageDimensions] = useState<{
    width: number;
    height: number;
    offsetX: number;
    offsetY: number;
    naturalWidth: number;
    naturalHeight: number;
  }>({
    width: 0,
    height: 0,
    offsetX: 0,
    offsetY: 0,
    naturalWidth: 0,
    naturalHeight: 0
  });
  const [editMode, setEditMode] = useState(false);
  const [slipFormData, setSlipFormData] = useState<SlipFormData>({
    slipNumber: '',
    renterName: '',
    boatName: '',
    boatType: '',
    phoneNumber: '',
    leaseEndDate: '',
  });
  const [dragDistance, setDragDistance] = useState(0);
  
  // Add new state for customer and vessel data
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [selectedVessel, setSelectedVessel] = useState<CustomerVessel | null>(null);
  const [loadingCustomerData, setLoadingCustomerData] = useState(false);
  
  // Add state for new slip dialog
  const [addSlipDialogOpen, setAddSlipDialogOpen] = useState(false);
  const [newSlipNumber, setNewSlipNumber] = useState('');
  const [newSlipError, setNewSlipError] = useState('');
  
  // Add state for GPS functionality
  const [userLocation, setUserLocation] = useState<GpsCoordinates | null>(null);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [isLocating, setIsLocating] = useState(false);
  const [nearestSlip, setNearestSlip] = useState<Slip | null>(null);
  const [marinaGpsBounds, setMarinaGpsBounds] = useState<MarinaGpsBounds>({
    northEast: { latitude: 0, longitude: 0 },
    southWest: { latitude: 0, longitude: 0 }
  });
  const [showLocationSnackbar, setShowLocationSnackbar] = useState(false);
  const [locationSnackbarMessage, setLocationSnackbarMessage] = useState('');
  
  // Add state for lock functionality
  const [slipsLocked, setSlipsLocked] = useState(true);
  
  // Add new state for contract data
  const [selectedContract, setSelectedContract] = useState<Contract | null>(null);
  
  // First, add a new state variable to track if the image is fully loaded
  const [imageFullyLoaded, setImageFullyLoaded] = useState(false);
  
  // Add state for reassign dialog and selection
  const [reassignDialogOpen, setReassignDialogOpen] = useState(false);
  const [reassignFromSlipId, setReassignFromSlipId] = useState<string>('');
  const [reassignToSlipId, setReassignToSlipId] = useState<string>('');
  const [reassignLoading, setReassignLoading] = useState(false);
  const [reassignError, setReassignError] = useState<string | null>(null);
  const [reassignSuccess, setReassignSuccess] = useState<string | null>(null);
  
  // Add state for vessel assignment
  const [assignVesselId, setAssignVesselId] = useState<string>('');
  const [assignVesselLoading, setAssignVesselLoading] = useState(false);
  const [assignVesselError, setAssignVesselError] = useState<string | null>(null);
  
  // Debug useEffect to log component mounting
  useEffect(() => {
    console.log('🚀 SlipManagementPage mounted from fallen-leaf-marina path');
  }, []);

  // Update containerSize whenever windowSize changes
  useEffect(() => {
    const updateContainerSize = () => {
      if (containerRef.current) {
        const { width, height } = containerRef.current.getBoundingClientRect();
        setContainerSize({ width, height });
        console.log(`Container size updated: ${width}x${height}`);
      }
    };
    
    // Update after a short delay to ensure the layout has adjusted
    const timeoutId = setTimeout(updateContainerSize, 100);
    return () => clearTimeout(timeoutId);
  }, [windowSize]);

  useEffect(() => {
    let unsubscribeSlips: () => void;
    let unsubscribeLaunches: () => void;
    
    const setupRealtimeUpdates = async () => {
      try {
        // Get marina settings to get background image
        const marinaSettings = await getMarinaSettings();
        setSettings(marinaSettings);
        if (marinaSettings.marinaImageBackground) {
          setMarinaImage(marinaSettings.marinaImageBackground);
        }
        
        // Set up real-time listeners
        const { collection, onSnapshot, query, where } = await import('firebase/firestore');
        const { db } = await import('../../../firebase/config');
        
        // Listen to slips collection
        const slipsQuery = query(collection(db, 'slips'));
        
        unsubscribeSlips = onSnapshot(slipsQuery, async (snapshot) => {
          const slipData = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          })) as Slip[];
          
          // For legacy data - ensure all slips have relative coordinates
          const defaultWidth = windowSize.width;
          const defaultHeight = windowSize.height;
          
          const updatedSlips = slipData.map(slip => {
            if (slip.relativeX === undefined || slip.relativeY === undefined) {
              // Calculate relative positions based on absolute positions and default container
              const relativeX = (slip.x / defaultWidth) * 100;
              const relativeY = (slip.y / defaultHeight) * 100;
              
              // Update the slip in the database
              const updatedSlip = { ...slip, relativeX, relativeY };
              upsertSlip(updatedSlip);
              
              return updatedSlip;
            }
            return slip;
          });
          
          // Only update if the data has actually changed
          setSlips(prevSlips => {
            const hasChanged = JSON.stringify(prevSlips) !== JSON.stringify(updatedSlips);
            return hasChanged ? updatedSlips : prevSlips;
          });
          
          // Only set loading to false on initial load
          if (loading) {
            setLoading(false);
          }
        }, (error) => {
          console.error('Error in slips listener:', error);
          if (loading) {
            setLoading(false);
          }
        });

        // Listen to launches collection for any changes
        const launchesQuery = query(collection(db, 'launches'));
        
        unsubscribeLaunches = onSnapshot(launchesQuery, async (snapshot) => {
          // When launches change, refresh the slips to get updated status
          const slipData = await getAllSlips();
          setSlips(prevSlips => {
            // Merge existing slip positions with new data
            const updatedSlips = slipData.map(newSlip => {
              const existingSlip = prevSlips.find(s => s.id === newSlip.id);
              return existingSlip ? { ...newSlip, x: existingSlip.x, y: existingSlip.y } : newSlip;
            });
            
            // Only update if the data has actually changed
            const hasChanged = JSON.stringify(prevSlips) !== JSON.stringify(updatedSlips);
            return hasChanged ? updatedSlips : prevSlips;
          });
        }, (error) => {
          console.error('Error in launches listener:', error);
        });
        
      } catch (error) {
        console.error('Error setting up real-time updates:', error);
        setLoading(false);
      }
    };
    
    setupRealtimeUpdates();
    
    // Cleanup subscriptions on unmount
    return () => {
      if (unsubscribeSlips) {
        unsubscribeSlips();
      }
      if (unsubscribeLaunches) {
        unsubscribeLaunches();
      }
    };
  }, [windowSize.width, windowSize.height]);

  // Effect to track window size with debouncing
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    
    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setWindowSize({
          width: window.innerWidth,
          height: window.innerHeight
        });
      }, 100); // Debounce resize events
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timeoutId);
    };
  }, []);
  
  // Optimize image URL based on current window size
  const optimizedImageUrl = useMemo(() => {
    return getOptimizedImageUrl(marinaImage, windowSize.width, windowSize.height);
  }, [marinaImage, windowSize.width, windowSize.height]);

  // Effect to update container and image dimensions when window size changes
  useEffect(() => {
    const updateDimensions = () => {
      if (!containerRef.current) return;
      
      const containerRect = containerRef.current.getBoundingClientRect();
      setContainerSize({ 
        width: containerRect.width, 
        height: containerRect.height 
      });
      
      // If we have the natural image dimensions, calculate the "contain" dimensions
      if (imageDimensions.naturalWidth && imageDimensions.naturalHeight) {
        const contained = calculateContainedImageDimensions(
          imageDimensions.naturalWidth,
          imageDimensions.naturalHeight,
          containerRect.width,
          containerRect.height
        );
        
        setImageDimensions({
          ...imageDimensions,
          ...contained
        });
        
        console.log('Updated image dimensions:', contained);
      }
    };
    
    // Update dimensions immediately and on resize
    updateDimensions();
    
    const debounceResize = () => {
      if (resizeTimeout.current) {
        clearTimeout(resizeTimeout.current);
      }
      resizeTimeout.current = setTimeout(updateDimensions, 100);
    };
    
    window.addEventListener('resize', debounceResize);
    return () => {
      window.removeEventListener('resize', debounceResize);
      if (resizeTimeout.current) {
        clearTimeout(resizeTimeout.current);
      }
    };
  }, [windowSize, imageDimensions.naturalWidth, imageDimensions.naturalHeight]);
  
  // Add a ref for resize timeout
  const resizeTimeout = useRef<NodeJS.Timeout | null>(null);

  // Update the handleAddSlip function to prompt for a slip number
  const handleAddSlip = () => {
    setNewSlipNumber('');
    setNewSlipError('');
    setAddSlipDialogOpen(true);
  };
  
  // Add function to create the slip after the dialog is confirmed
  const handleCreateSlip = async () => {
    if (!newSlipNumber.trim()) {
      setNewSlipError('Please enter a slip number');
      return;
    }
    
    if (!imageDimensions.naturalWidth || !imageDimensions.naturalHeight) {
      console.error("Image dimensions not available yet");
      setNewSlipError("Map is not fully loaded. Please try again.");
      return;
    }
    
    // Check if slip number already exists
    const existingSlip = slips.find(slip => slip.slipNumber === newSlipNumber);
    if (existingSlip) {
      setNewSlipError(`Slip number ${newSlipNumber} already exists`);
      return;
    }
    
    // Default position at 30% from left and 30% from top of the actual image
    const x = imageDimensions.naturalWidth * 0.3;
    const y = imageDimensions.naturalHeight * 0.3;
    
    const newId = generateRandomId();
    const newSlip = { 
      id: newId, 
      x, 
      y,
      slipNumber: newSlipNumber
    };
    
    try {
      // Ensure newSlip has all required properties
      const completeSlip: Slip = {
        id: newSlip.id,
        x: newSlip.x,
        y: newSlip.y,
        status: 'available',
        type: 'standard',
        length: 0,
        width: 0,
        depth: 0,
        dock: 'Main',
        slipNumber: newSlip.slipNumber || `Slip ${newSlip.id}`,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      // First update the UI for immediate feedback
      setSlips(prev => [...prev, completeSlip]);
      
      // Then save to database
      await upsertSlip(completeSlip);
      
      setAddSlipDialogOpen(false);
    } catch (error) {
      console.error('Error creating slip:', error);
      setNewSlipError('Failed to create slip. Please try again.');
    }
  };

  const handleDeleteSlip = async (id: string) => {
    try {
      console.log(`Attempting to delete slip ${id}...`);
      
      // First update the UI for immediate feedback
      setSlips(prev => {
        console.log("Filtering out slip:", id);
        return prev.filter(slip => slip.id !== id);
      });
      
      // Then attempt to delete from the database
      console.log("Calling deleteSlip API for slip:", id);
      await deleteSlip(id);
      
      console.log(`Successfully deleted slip ${id}`);
    } catch (error) {
      console.error(`Error deleting slip ${id}:`, error);
      
      // If deletion fails, restore the slip in the UI
      const fetchData = async () => {
        try {
          const slipData = await getAllSlips();
          setSlips(slipData);
        } catch (fetchError) {
          console.error('Error refreshing slip data:', fetchError);
        }
      };
      
      fetchData();
      
      // Show error message to user
      alert(`Failed to delete slip ${id}. ${error instanceof Error ? error.message : 'Unknown error occurred.'}`);
    }
  };

  const handleImageError = () => {
    setImageError(true);
    console.log('Background image failed to load, using gradient background');
  };

  const handleImageSelect = async (image: ImageMetadata) => {
    try {
      console.log('Selecting image:', image);
      console.log('Current settings:', settings);
      
      // Update the marina settings with the new image URL
      const updatedSettings = {
        ...settings,
        marinaImageBackground: image.storageUrl
      };
      
      console.log('Updating settings to:', updatedSettings);
      await updateMarinaSettings(updatedSettings);
      
      // Update local state
      setMarinaImage(image.storageUrl);
      setImageError(false);
      setOpenImageSelector(false);
      
      console.log('Updated settings successfully');
    } catch (error) {
      console.error('Error updating marina image:', error);
    }
  };

  const handleSlipClick = async (slip: Slip) => {
    // Don't open details if actively dragging
    if (activeId === slip.id) {
      console.log("Ignoring click while dragging");
      return;
    }
    
    console.log("handleSlipClick called for slip:", slip.id);
    
    // Clear any existing search results
    setSearchResults([]);
    setShowSearchResults(false);
    
    // Set the selected slip first
    setSelectedSlip(slip);
    
    // Initialize form data with the current slip information
    setSlipFormData({
      slipNumber: slip.slipNumber || '',
      renterName: slip.renterName || '',
      boatName: slip.boatName || '',
      boatType: slip.boatType || '',
      phoneNumber: slip.phoneNumber || '',
      leaseEndDate: slip.leaseEndDate || '',
    });
    
    // Reset other states
    setSelectedCustomer(null);
    setSelectedVessel(null);
    setSelectedContract(null);
    
    // Fetch customer data if customerId exists
    if (slip.customerId) {
      setLoadingCustomerData(true);
      try {
        const customer = await getCustomerById(slip.customerId);
        setSelectedCustomer(customer);
        
        // Find assigned vessel if assignedVesselId exists
        if (slip.assignedVesselId && customer?.vessels) {
          const vessel = customer.vessels.find(v => v.vesselId === slip.assignedVesselId);
          if (vessel) {
            setSelectedVessel(vessel);
          }
        }
      } catch (error) {
        console.error("Error fetching customer/vessel data:", error);
      } finally {
        setLoadingCustomerData(false);
      }
    }

    // Fetch contract data if contractId exists
    if (slip.contractId) {
      try {
        const contracts = await getContractsBySlip(slip.id);
        if (contracts.length > 0) {
          setSelectedContract(contracts[0]); // Get the most recent contract
        }
      } catch (error) {
        console.error("Error fetching contract data:", error);
      }
    }
    
    setEditMode(false); // Start in view mode
    console.log("Dialog should open with selectedSlip:", slip.id);
  };

  const handleCloseSlipDetails = () => {
    console.log("Closing slip details dialog");
    setSelectedSlip(null);
    setSelectedCustomer(null);
    setSelectedVessel(null);
    setSelectedContract(null);
  };

  // Simplified orientation change handler
  useEffect(() => {
    const handleOrientationChange = () => {
      console.log('Orientation change detected');
      
      // Reset the image fully loaded flag
      setImageFullyLoaded(false);
      
      // Update window size state
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight
      });
      
      // Force recalculation after orientation change
      if (containerRef.current) {
        const containerRect = containerRef.current.getBoundingClientRect();
        
        // Update container size
        setContainerSize({ 
          width: containerRect.width, 
          height: containerRect.height 
        });
        
        // Update image dimensions if we have natural dimensions
        if (imageDimensions.naturalWidth && imageDimensions.naturalHeight) {
          // Calculate height based on width to maintain aspect ratio
          const height = (containerRect.width / imageDimensions.naturalWidth) * imageDimensions.naturalHeight;
          
          setImageDimensions(prev => ({
            ...prev,
            width: containerRect.width,
            height: height,
          }));
          
          // Mark as fully loaded again
          setTimeout(() => setImageFullyLoaded(true), 100);
        }
      }
    };
    
    // Listen for orientation changes
    window.addEventListener('orientationchange', handleOrientationChange);
    
    // Also listen for resize events to catch orientation changes on devices that don't fire orientationchange
    window.addEventListener('resize', handleOrientationChange);
    
    return () => {
      window.removeEventListener('orientationchange', handleOrientationChange);
      window.removeEventListener('resize', handleOrientationChange);
    };
  }, [imageDimensions.naturalWidth, imageDimensions.naturalHeight]);

  // Update slipToViewportCoordinates function
  const slipToViewportCoordinates = (slip: Slip): { left: string, top: string } => {
    if (!imageDimensions.width || !imageDimensions.height || 
        !imageDimensions.naturalWidth || !imageDimensions.naturalHeight) {
      return { left: '0px', top: '0px' };
    }

    // Prevent division by zero by using max(1, value)
    const naturalWidth = Math.max(1, imageDimensions.naturalWidth);
    const naturalHeight = Math.max(1, imageDimensions.naturalHeight);

    // Convert absolute image coordinates to viewport coordinates
    const left = (slip.x / naturalWidth) * imageDimensions.width;
    const top = (slip.y / naturalHeight) * imageDimensions.height;

    return {
      left: `${left}px`,
      top: `${top}px`
    };
  };
  
  // Update the handleDragEnd function to work with the new HTML elements
  const handleDragEnd = async (event: any) => {
    if (!imageDimensions.naturalWidth || !imageDimensions.naturalHeight) return;
    
    const { active, delta } = event;
    if (!active) return;
    
    // We need to scale the delta to match the image coordinates
    const scaleX = imageDimensions.naturalWidth / Math.max(imageDimensions.width, 1);
    const scaleY = imageDimensions.naturalHeight / Math.max(imageDimensions.height, 1);
    
    // Scale the pixel movement to image coordinates
    const scaledDelta = {
      x: delta.x * scaleX,
      y: delta.y * scaleY
    };
    
    setSlips((prev) => {
      const updated = prev.map((slip) => {
        if (slip.id === active.id) {
          // Apply the scaled delta directly to the absolute coordinates
          const updatedSlip = {
            ...slip,
            x: slip.x + scaledDelta.x,
            y: slip.y + scaledDelta.y,
          };
          
          // Persist the updated slip
          upsertSlip(updatedSlip);
          
          return updatedSlip;
        }
        return slip;
      });
      return updated;
    });
    
    setActiveId(null);
  };

  // Update handleFormChange to work with all form element types
  const handleFormChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<string>
  ) => {
    const name = e.target.name;
    const value = e.target.value;
    
    if (name) {
      setSlipFormData(prev => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  // Update the handleSaveSlip function to save to Firebase
  const handleSaveSlip = async () => {
    if (!selectedSlip) return;
    
    try {
      // Create the updated slip with all form data
      const updatedSlip: Slip = {
        ...selectedSlip,
        slipNumber: slipFormData.slipNumber,
        renterName: slipFormData.renterName,
        boatName: slipFormData.boatName,
        boatType: slipFormData.boatType,
        phoneNumber: slipFormData.phoneNumber,
        leaseEndDate: slipFormData.leaseEndDate,
      };
      
      // First update locally for immediate feedback
      setSlips(prev => 
        prev.map(slip => 
          slip.id === selectedSlip.id ? updatedSlip : slip
        )
      );
      
      // Then save to Firebase
      console.log("Saving slip to Firebase:", updatedSlip);
      await upsertSlip(updatedSlip);
      
      // Show success message
      alert(`Successfully updated slip ${slipFormData.slipNumber}`);
      
      // Turn off edit mode
      setEditMode(false);
    } catch (error) {
      console.error("Error saving slip data:", error);
      alert(`Error saving slip: ${error instanceof Error ? error.message : 'Unknown error occurred'}`);
    }
  };

  // Add function to toggle edit mode
  const toggleEditMode = () => {
    setEditMode(prev => !prev);
  };

  // Load marina GPS bounds from settings
  useEffect(() => {
    if (settings && settings.marinaGpsBounds) {
      setMarinaGpsBounds(settings.marinaGpsBounds);
    }
  }, [settings]);
  
  // Function to get user's current location
  const getUserLocation = () => {
    if (!navigator.geolocation) {
      setLocationError("Geolocation is not supported by your browser");
      setLocationSnackbarMessage("Geolocation is not supported by your browser");
      setShowLocationSnackbar(true);
      return;
    }
    
    setIsLocating(true);
    setLocationError(null);
    
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude, accuracy } = position.coords;
        setUserLocation({ latitude, longitude, accuracy });
        setIsLocating(false);
        
        // Check if user is within marina bounds
        if (
          latitude <= marinaGpsBounds.northEast.latitude &&
          latitude >= marinaGpsBounds.southWest.latitude &&
          longitude <= marinaGpsBounds.northEast.longitude &&
          longitude >= marinaGpsBounds.southWest.longitude
        ) {
          findNearestSlip({ latitude, longitude, accuracy });
        } else {
          setLocationSnackbarMessage("You appear to be outside the marina area");
          setShowLocationSnackbar(true);
        }
      },
      (error) => {
        setIsLocating(false);
        let errorMessage = "Unknown error";
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = "Permission to access location was denied";
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = "Location information is unavailable";
            break;
          case error.TIMEOUT:
            errorMessage = "The request to get user location timed out";
            break;
        }
        setLocationError(errorMessage);
        setLocationSnackbarMessage(errorMessage);
        setShowLocationSnackbar(true);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  };
  
  // Function to find the nearest slip to user's location
  const findNearestSlip = (location: GpsCoordinates) => {
    if (!slips.length || !location || !settings?.slipsGpsData) {
      return;
    }
    
    // Find the nearest slip
    let nearestDistance = Infinity;
    let closest: Slip | null = null;
    
    slips.forEach(slip => {
      // If we have GPS data for this slip
      if (settings.slipsGpsData && settings.slipsGpsData[slip.id]) {
        const slipGps = settings.slipsGpsData[slip.id];
        const distance = calculateDistance(
          location.latitude, 
          location.longitude, 
          slipGps.latitude, 
          slipGps.longitude
        );
        
        if (distance < nearestDistance) {
          nearestDistance = distance;
          closest = slip;
        }
      }
    });
    
    if (closest) {
      setNearestSlip(closest);
      // Focus on this slip and open its details
      handleSlipClick(closest);
      
      setLocationSnackbarMessage(`Found nearest slip: ${closest.slipNumber || closest.id} (${nearestDistance.toFixed(1)}m away)`);
      setShowLocationSnackbar(true);
    } else {
      setLocationSnackbarMessage("No slips with GPS data found");
      setShowLocationSnackbar(true);
    }
  };
  
  // Function to save GPS data for a slip
  const saveSlipGpsData = async (slipId: string, latitude: number, longitude: number) => {
    if (!settings) return;
    
    try {
      // Create updated settings with the new slip GPS data
      const updatedSettings = {
        ...settings,
        slipsGpsData: {
          ...(settings.slipsGpsData || {}),
          [slipId]: { latitude, longitude }
        }
      };
      
      await updateMarinaSettings(updatedSettings);
      setSettings(updatedSettings);
      
      setLocationSnackbarMessage(`GPS data saved for slip ${slipId}`);
      setShowLocationSnackbar(true);
    } catch (error) {
      console.error('Error saving slip GPS data:', error);
      setLocationSnackbarMessage("Failed to save GPS data");
      setShowLocationSnackbar(true);
    }
  };
  
  // Function to set marina GPS bounds
  const setMarinaGpsBoundsFromCurrentLocation = async () => {
    if (!userLocation) {
      setLocationSnackbarMessage("No location available");
      setShowLocationSnackbar(true);
      return;
    }
    
    try {
      // Create a 500m square around current position for demo purposes
      // In production, this would be set more precisely
      const latOffset = 0.0045; // Approximately 500m
      const lonOffset = 0.0045 / Math.cos(userLocation.latitude * Math.PI/180);
      
      const newBounds = {
        northEast: {
          latitude: userLocation.latitude + latOffset,
          longitude: userLocation.longitude + lonOffset
        },
        southWest: {
          latitude: userLocation.latitude - latOffset,
          longitude: userLocation.longitude - lonOffset
        }
      };
      
      // Update settings
      const updatedSettings = {
        ...settings,
        marinaGpsBounds: newBounds
      };
      
      await updateMarinaSettings(updatedSettings);
      setSettings(updatedSettings);
      setMarinaGpsBounds(newBounds);
      
      setLocationSnackbarMessage("Marina GPS bounds set");
      setShowLocationSnackbar(true);
    } catch (error) {
      console.error('Error setting marina GPS bounds:', error);
      setLocationSnackbarMessage("Failed to set marina bounds");
      setShowLocationSnackbar(true);
    }
  };
  
  // Add a location button to the selected slip dialog
  const handleAssignLocationToSlip = async () => {
    if (!userLocation || !selectedSlip) return;
    
    await saveSlipGpsData(selectedSlip.id, userLocation.latitude, userLocation.longitude);
  };

  // Add this function to handle lock toggle
  const handleToggleLock = () => {
    setSlipsLocked(!slipsLocked);
  };

  // Add this function to check if user is admin
  const isAdmin = () => {
    return userProfile?.role?.name?.toLowerCase() === 'admin';
  };

  // Update the navigation drawer to include the lock button
  const drawerContent = (
    <List>
      {isAdmin() && (
        <>
          <ListItemButton onClick={handleAddSlip}>
            <ListItemIcon>
              <AddIcon />
            </ListItemIcon>
            <ListItemText primary="Add Slip" />
          </ListItemButton>
          
          <ListItemButton onClick={handleToggleLock}>
            <ListItemIcon>
              {slipsLocked ? <LockIcon color="primary" /> : <LockOpenIcon />}
            </ListItemIcon>
            <ListItemText primary={slipsLocked ? "Unlock Slips" : "Lock Slips"} />
          </ListItemButton>
          
          <ListItemButton onClick={() => setReassignDialogOpen(true)}>
            <ListItemIcon>
              <SwapHorizIcon />
            </ListItemIcon>
            <ListItemText primary="Re-Assign Vessel" />
          </ListItemButton>
          
          <Divider sx={{ my: 2 }} />
          
          <ListItemButton component={Link} to="/admin/dashboard">
            <ListItemIcon>
              <DashboardIcon />
            </ListItemIcon>
            <ListItemText primary="Dashboard" />
          </ListItemButton>
          
          <ListItemButton component={Link} to="/admin/settings">
            <ListItemIcon>
              <SettingsIcon />
            </ListItemIcon>
            <ListItemText primary="Settings" />
          </ListItemButton>
          <ListItemButton component={Link} to="/admin/marina/launch-activity">
            <ListItemIcon>
              <DirectionsBoatIcon />
            </ListItemIcon>
            <ListItemText primary="Launch Activity" />
          </ListItemButton>
        </>
      )}

      <Divider sx={{ my: 2 }} />
      
      <ListItemButton component={Link} to="/staff/dashboard">
        <ListItemIcon>
          <HomeIcon />
        </ListItemIcon>
        <ListItemText primary="Home" />
      </ListItemButton>
      
      <ListItemButton onClick={() => window.location.href = '/auth/logout'}>
        <ListItemIcon>
          <LogoutIcon />
        </ListItemIcon>
        <ListItemText primary="Logout" />
      </ListItemButton>
    </List>
  );

  // Update the getSlipRenterInfo function to use the slip's slipNumber directly
  const getSlipRenterInfo = (slipId: string) => {
    const slip = slips.find(s => s.id === slipId);
    
    if (slip) {
      return {
        slipNumber: slip.slipNumber || 'Unassigned',
        renterName: slip.renterName || 'Unoccupied',
        boatName: slip.boatName || 'N/A',
        boatType: slip.boatType || 'N/A',
        phoneNumber: slip.phoneNumber || 'N/A',
        leaseEndDate: slip.leaseEndDate || 'N/A'
      };
    }
    
    // Default for empty slips
    return { 
      slipNumber: 'Unassigned',
      renterName: 'Unoccupied',
      boatName: 'N/A',
      boatType: 'N/A',
      phoneNumber: 'N/A',
      leaseEndDate: 'N/A'
    };
  };

  // Debug and fix the selectedSlip state handling
  // Add this useEffect at the bottom of the main component's effect hooks
  useEffect(() => {
    console.log("selectedSlip changed:", selectedSlip ? selectedSlip.id : 'null');
  }, [selectedSlip]);

  // Modify the handleImageLoad function to be more robust for mobile devices
  const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.target as HTMLImageElement;
    console.log('Background image loaded successfully');
    
    // Store the natural dimensions of the image with fallbacks
    const imgNaturalWidth = img.naturalWidth || 1000; 
    const imgNaturalHeight = img.naturalHeight || 750;
    
    // Calculate dimensions based on the image's natural size
    if (containerRef.current) {
      const containerRect = containerRef.current.getBoundingClientRect();
      
      console.log('Container dimensions:', containerRect.width, containerRect.height);
      console.log('Image natural dimensions:', imgNaturalWidth, imgNaturalHeight);
      
      setImageDimensions({
        width: containerRect.width,
        height: containerRect.width * (imgNaturalHeight / imgNaturalWidth),
        offsetX: 0,
        offsetY: 0,
        naturalWidth: imgNaturalWidth,
        naturalHeight: imgNaturalHeight
      });
      
      setContainerSize({ 
        width: containerRect.width, 
        height: containerRect.height 
      });
      
      // Mark image as fully loaded
      setImageFullyLoaded(true);
    }
  };

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Slip[]>([]);
  const [showSearchResults, setShowSearchResults] = useState(false);

  // Add state for customer data cache
  const [customerDataCache, setCustomerDataCache] = useState<Record<string, Customer>>({});

  // Pre-fetch customer data for all slips
  useEffect(() => {
    const fetchCustomerData = async () => {
      const customerIds = new Set<string>();
      slips.forEach(slip => {
        if (slip.customerId) {
          customerIds.add(slip.customerId);
        }
      });

      const newCache: Record<string, Customer> = {};
      for (const customerId of customerIds) {
        try {
          const customer = await getCustomerById(customerId);
          if (customer) {
            newCache[customerId] = customer;
          }
        } catch (error) {
          console.error('Error fetching customer data:', error);
        }
      }
      setCustomerDataCache(newCache);
    };

    fetchCustomerData();
  }, [slips]);

  // Add search function
  const handleSearch = (query: string) => {
    if (!query.trim()) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }

    const searchTerm = query.toLowerCase().trim();
    const results: Slip[] = [];

    // Search through slips using cached customer data
    for (const slip of slips) {
      if (slip.customerId) {
        const customer = customerDataCache[slip.customerId];
        if (customer) {
          // Check if customer name matches
          const customerName = `${customer.firstName || ''} ${customer.lastName || ''}`.toLowerCase().trim();
          const companyName = (customer.companyName || '').toLowerCase().trim();
          
          // Check if vessel matches (if there's an assigned vessel)
          let vesselMatches = false;
          if (slip.assignedVesselId && customer.vessels) {
            const matchingVessel = customer.vessels.find(vessel => 
              vessel.vesselId === slip.assignedVesselId && (
                vessel.registrationNumber?.toLowerCase().includes(searchTerm) ||
                vessel.name?.toLowerCase().includes(searchTerm)
              )
            );
            vesselMatches = !!matchingVessel;
          }

          // Add to results if either customer name or vessel matches
          if (customerName.includes(searchTerm) || 
              companyName.includes(searchTerm) || 
              vesselMatches) {
            results.push(slip);
          }
        }
      }
    }

    // Update search results
    setSearchResults(results);
    setShowSearchResults(true);

    // If exactly one result is found, open its details
    if (results.length === 1) {
      // Clear search results first
      setSearchResults([]);
      setShowSearchResults(false);
      // Then open the slip details
      const slipToOpen = results[0];
      // Ensure we have the latest slip data
      const updatedSlip = slips.find(s => s.id === slipToOpen.id) || slipToOpen;
      handleSlipClick(updatedSlip);
    }
  };

  // Add handler for Enter key and go button
  const handleSearchTrigger = () => {
    if (searchQuery.trim()) {
      handleSearch(searchQuery);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleSearchTrigger();
    }
  };

  // Memoize the search results to prevent unnecessary re-renders
  const memoizedSearchResults = useMemo(() => {
    return searchResults.map((slip) => (
      <ListItemButton
        key={slip.id}
        onClick={() => {
          handleSlipClick(slip);
          setShowSearchResults(false);
        }}
      >
        <ListItemText
          primary={`Slip ${slip.slipNumber || slip.id}`}
          secondary="Click to view details"
        />
      </ListItemButton>
    ));
  }, [searchResults]);

  return loading ? (
    <Box sx={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      height: '100vh', 
      width: '100vw',
      backgroundColor: 'white',
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 9999
    }}>
      <CircularProgress />
    </Box>
  ) : (
    <ThemeProvider theme={theme}>
      <FullScreenLayout drawerContent={drawerContent} startCollapsed={true}>
        <Box 
          sx={{ 
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100%',
            height: '100%',
            zIndex: 9000,
            overflow: 'hidden',
            display: 'block',
            backgroundColor: 'black',
          }}
        >
          {/* Add Search Bar */}
          <Box
            sx={{
              position: 'absolute',
              top: 16,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 9100,
              width: '90%',
              maxWidth: 400,
            }}
          >
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search by vessel registration, name, or customer..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={handleKeyPress}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    {searchQuery && (
                      <>
                        <IconButton
                          size="small"
                          onClick={handleSearchTrigger}
                          sx={{ mr: 1 }}
                        >
                          <ArrowForwardIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSearchQuery('');
                            setSearchResults([]);
                            setShowSearchResults(false);
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </>
                    )}
                  </InputAdornment>
                ),
                sx: {
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  borderRadius: 1,
                }
              }}
            />
            {showSearchResults && searchResults.length > 0 && (
              <Paper
                sx={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  mt: 1,
                  maxHeight: 300,
                  overflow: 'auto',
                  zIndex: 9101,
                }}
              >
                <List>
                  {memoizedSearchResults}
                </List>
              </Paper>
            )}
          </Box>

          <Box sx={{ 
            position: 'relative', 
            width: '100%', 
            height: '100%',
            overflow: 'auto',
            display: 'block',
            backgroundColor: 'black',
          }}>
            <DndContext 
              onDragEnd={handleDragEnd} 
              onDragStart={e => {
                if (slipsLocked) {
                  return;
                }
                setActiveId(e.active.id as string);
                setDragDistance(0);
              }}
              modifiers={slipsLocked ? [() => ({ x: 0, y: 0, scaleX: 1, scaleY: 1 })] : undefined}
            >
              <Box
                ref={containerRef}
                sx={{
                  position: 'relative',
                  width: '100%',
                  height: '100%',
                  background: 'black',
                  margin: 0,
                  padding: 0,
                  zIndex: 1,
                  overflow: 'visible',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                {!imageError && (
                  <Box 
                    component="img"
                    src={optimizedImageUrl}
                    alt="Marina Background"
                    sx={{
                      width: '100%',
                      height: 'auto',
                      objectFit: 'contain',
                      objectPosition: 'center',
                      display: 'block',
                    }}
                    onError={handleImageError}
                    onLoad={handleImageLoad}
                  />
                )}
                
                {imageError && (
                  <Alert 
                    severity="info" 
                    sx={{ 
                      position: 'absolute', 
                      bottom: 16, 
                      left: '50%',
                      transform: 'translateX(-50%)',
                      zIndex: 100,
                      opacity: 0.9,
                      maxWidth: '90%'
                    }}
                  >
                    Background image not available. Using gradient background.
                  </Alert>
                )}
                
                {/* HTML overlay for slip markers - only show when fully loaded */}
                {imageDimensions.naturalWidth > 0 && imageDimensions.naturalHeight > 0 && imageFullyLoaded && (
                  <div
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      pointerEvents: 'all',
                      zIndex: 2,
                      overflow: 'visible',
                    }}
                  >
                    {slips.map((slip) => (
                      <HTMLSlipMarker
                        key={slip.id}
                        slip={slip}
                        onDelete={() => handleDeleteSlip(slip.id)}
                        onSlipClick={handleSlipClick}
                        containerSize={containerSize}
                        dragDistance={dragDistance}
                        slipsLocked={slipsLocked}
                        imageDimensions={imageDimensions}
                      />
                    ))}
                  </div>
                )}
              </Box>
            </DndContext>

            {/* Image Selection Dialog */}
            <Dialog 
              open={openImageSelector} 
              onClose={() => setOpenImageSelector(false)}
              maxWidth="md"
              fullWidth
            >
              <DialogTitle>Select Marina Background Image</DialogTitle>
              <DialogContent>
                <ImageManager
                  category="MARINA"
                  onSelect={handleImageSelect}
                  showPublicOnly={true}
                />
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setOpenImageSelector(false)}>Cancel</Button>
              </DialogActions>
            </Dialog>

            {/* Slip Details Dialog */}
            <Dialog
              open={selectedSlip !== null}
              onClose={handleCloseSlipDetails}
              maxWidth="sm"
              fullWidth
              BackdropProps={{
                style: { backgroundColor: 'rgba(0, 0, 0, 0.7)' },
              }}
              PaperProps={{
                style: { boxShadow: '0px 0px 30px rgba(0, 0, 0, 0.5)' },
              }}
              style={{ zIndex: 9999, position: 'fixed' }}
            >
              {selectedSlip ? (
                <>
                  <DialogTitle sx={{ pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      {selectedSlip.slipNumber || 'Unassigned Slip'}
                    </div>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {userLocation && (
                        <Tooltip title="Set slip location to your current position">
                          <IconButton 
                            size="small" 
                            color="primary"
                            onClick={handleAssignLocationToSlip}
                          >
                            <MyLocationIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      {isAdmin() && (
                        <Button 
                          onClick={toggleEditMode}
                          color={editMode ? "secondary" : "primary"}
                        >
                          {editMode ? "Cancel Edit" : "Edit"}
                        </Button>
                      )}
                    </Box>
                  </DialogTitle>
                  <DialogContent>
                    <Box sx={{ my: 2 }}>
                      {editMode ? (
                        <Box component="form" sx={{ mt: 2 }}>
                          <TextField
                            fullWidth
                            margin="normal"
                            label="Slip Number"
                            name="slipNumber"
                            value={slipFormData.slipNumber}
                            onChange={handleFormChange}
                            placeholder="Enter slip number"
                            variant="outlined"
                          />
                        </Box>
                      ) : (
                        <>
                          {/* Contract Information */}
                          {selectedContract && (
                            <>
                              <Typography variant="h6" gutterBottom sx={{ mt: 0, display: 'flex', alignItems: 'center' }}>
                                <DescriptionIcon sx={{ mr: 1 }} /> Contract Information
                              </Typography>
                              <Card variant="outlined" sx={{ mb: 3 }}>
                                <CardContent>
                                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 1 }}>
                                    <Typography variant="body2" color="text.secondary">
                                      Start Date:
                                    </Typography>
                                    <Typography variant="body2">
                                      {format(selectedContract.startDate, 'MM/dd/yyyy')}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                      End Date:
                                    </Typography>
                                    <Typography variant="body2">
                                      {format(selectedContract.endDate, 'MM/dd/yyyy')}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                      Payment Status:
                                    </Typography>
                                    <Typography variant="body2">
                                      {capitalizeWords(selectedContract.paymentStatus)}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                      Payment Date:
                                    </Typography>
                                    <Typography variant="body2">
                                      {selectedContract.paymentReceivedDate ? format(selectedContract.paymentReceivedDate, 'MM/dd/yyyy') : 'N/A'}
                                    </Typography>
                                  </Box>
                                </CardContent>
                              </Card>
                            </>
                          )}

                          {/* Customer Information */}
                          <Typography variant="h6" gutterBottom sx={{ mt: 3, display: 'flex', alignItems: 'center' }}>
                            <PersonIcon sx={{ mr: 1 }} /> Customer Information
                          </Typography>
                          
                          {selectedCustomer ? (
                            <Card variant="outlined" sx={{ mb: 3 }}>
                              <CardContent>
                                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                                  <Typography variant="body2" color="text.secondary">
                                    Name:
                                  </Typography>
                                  <Typography variant="body2">
                                    {selectedCustomer.firstName || selectedCustomer.lastName 
                                      ? `${selectedCustomer.firstName || ''} ${selectedCustomer.lastName || ''}`.trim()
                                      : selectedCustomer.companyName || 'N/A'}
                                  </Typography>
                                  
                                  <Typography variant="body2" color="text.secondary">
                                    Email:
                                  </Typography>
                                  <Typography variant="body2">
                                    {selectedCustomer.email}
                                  </Typography>
                                  
                                  <Typography variant="body2" color="text.secondary">
                                    Phone:
                                  </Typography>
                                  <Typography variant="body2">
                                    {formatPhoneNumber(selectedCustomer.phone)}
                                  </Typography>
                                </Box>
                              </CardContent>
                            </Card>
                          ) : (
                            <Card variant="outlined" sx={{ mb: 3, bgcolor: 'action.hover' }}>
                              <CardContent>
                                <Box sx={{ textAlign: 'center', py: 1 }}>
                                  <PersonAddIcon color="disabled" sx={{ fontSize: 40, mb: 1 }} />
                                  <Typography variant="body2" color="text.secondary">
                                    No customer assigned to this slip
                                  </Typography>
                                </Box>
                              </CardContent>
                            </Card>
                          )}
                          
                          {/* Vessel Information */}
                          <Typography variant="h6" gutterBottom sx={{ mt: 3, display: 'flex', alignItems: 'center' }}>
                            <DirectionsBoatIcon sx={{ mr: 1 }} /> Vessel Information
                          </Typography>
                          
                          {selectedVessel ? (
                            <Card variant="outlined" sx={{ mb: 3 }}>
                              <CardContent>
                                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                                  <Typography variant="body2" color="text.secondary">
                                    Registration:
                                  </Typography>
                                  <Typography variant="body2">
                                    {selectedVessel.registrationNumber}
                                  </Typography>
                                  
                                  <Typography variant="body2" color="text.secondary">
                                    Name:
                                  </Typography>
                                  <Typography variant="body2">
                                    {selectedVessel.name || 'Unnamed'}
                                  </Typography>
                                  
                                  <Typography variant="body2" color="text.secondary">
                                    Type:
                                  </Typography>
                                  <Typography variant="body2">
                                    {selectedVessel.type}
                                  </Typography>
                                  
                                  <Typography variant="body2" color="text.secondary">
                                    Make/Model:
                                  </Typography>
                                  <Typography variant="body2">
                                    {selectedVessel.make} {selectedVessel.model}
                                  </Typography>
                                  
                                  <Typography variant="body2" color="text.secondary">
                                    Year:
                                  </Typography>
                                  <Typography variant="body2">
                                    {selectedVessel.year}
                                  </Typography>
                                  
                                  <Typography variant="body2" color="text.secondary">
                                    Length:
                                  </Typography>
                                  <Typography variant="body2">
                                    {selectedVessel.length} ft
                                  </Typography>
                                  
                                  <Typography variant="body2" color="text.secondary">
                                    Color:
                                  </Typography>
                                  <Typography variant="body2">
                                    {selectedVessel.color}
                                  </Typography>
                                </Box>
                              </CardContent>
                            </Card>
                          ) : selectedCustomer ? (
                            <Card variant="outlined" sx={{ mb: 3, bgcolor: 'action.hover' }}>
                              <CardContent>
                                <Box sx={{ textAlign: 'center', py: 1 }}>
                                  <DirectionsBoatIcon color="disabled" sx={{ fontSize: 40, mb: 1 }} />
                                  <Typography variant="body2" color="text.secondary">
                                    No vessel assigned to this slip
                                  </Typography>
                                  {selectedCustomer.vessels && selectedCustomer.vessels.length > 0 && (
                                    <>
                                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                        This customer has {selectedCustomer.vessels.length} vessel(s) available for assignment
                                      </Typography>
                                      {isAdmin() && (
                                        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                                          <FormControl fullWidth sx={{ maxWidth: 300 }}>
                                            <InputLabel>Select Vessel</InputLabel>
                                            <Select
                                              value={assignVesselId}
                                              label="Select Vessel"
                                              onChange={e => setAssignVesselId(e.target.value)}
                                            >
                                              {selectedCustomer.vessels.map(vessel => (
                                                <MenuItem key={vessel.vesselId} value={vessel.vesselId}>
                                                  {vessel.registrationNumber} - {vessel.name || vessel.type || 'Unnamed'}
                                                </MenuItem>
                                              ))}
                                            </Select>
                                          </FormControl>
                                          <Button
                                            variant="contained"
                                            color="primary"
                                            disabled={!assignVesselId || assignVesselLoading}
                                            onClick={async () => {
                                              setAssignVesselLoading(true);
                                              setAssignVesselError(null);
                                              try {
                                                if (!selectedSlip) return;
                                                const updatedSlip = { ...selectedSlip, assignedVesselId: assignVesselId, launchStatus: 'not_launched' as const };
                                                await upsertSlip(updatedSlip);
                                                setSlips(prev => prev.map(slip => slip.id === updatedSlip.id ? updatedSlip : slip));
                                                setAssignVesselId('');
                                                setSelectedVessel(selectedCustomer.vessels.find(v => v.vesselId === assignVesselId) || null);
                                              } catch (err: any) {
                                                setAssignVesselError('Failed to assign vessel. ' + (err?.message || ''));
                                              } finally {
                                                setAssignVesselLoading(false);
                                              }
                                            }}
                                          >
                                            {assignVesselLoading ? <CircularProgress size={20} /> : 'Assign Vessel'}
                                          </Button>
                                          {assignVesselError && <Alert severity="error">{assignVesselError}</Alert>}
                                        </Box>
                                      )}
                                    </>
                                  )}
                                </Box>
                              </CardContent>
                            </Card>
                          ) : (
                            <Card variant="outlined" sx={{ mb: 3, bgcolor: 'action.hover' }}>
                              <CardContent>
                                <Box sx={{ textAlign: 'center', py: 1 }}>
                                  <DirectionsBoatIcon color="disabled" sx={{ fontSize: 40, mb: 1 }} />
                                  <Typography variant="body2" color="text.secondary">
                                    No vessel assigned to this slip
                                  </Typography>
                                </Box>
                              </CardContent>
                            </Card>
                          )}
                        </>
                      )}
                    </Box>
                  </DialogContent>
                  <DialogActions>
                    <Button 
                      onClick={handleCloseSlipDetails} 
                      color="secondary"
                    >
                      Close
                    </Button>
                    {editMode && (
                      <Button 
                        onClick={handleSaveSlip} 
                        color="primary"
                        variant="contained"
                        startIcon={<SaveIcon />}
                      >
                        Save Changes
                      </Button>
                    )}
                  </DialogActions>
                </>
              ) : (
                <div>No slip selected</div>
              )}
            </Dialog>

            {/* Add Slip Dialog */}
            <Dialog
              open={addSlipDialogOpen}
              onClose={() => setAddSlipDialogOpen(false)}
              maxWidth="xs"
              fullWidth
            >
              <DialogTitle>Add New Slip</DialogTitle>
              <DialogContent>
                <TextField
                  autoFocus
                  margin="dense"
                  label="Slip Number"
                  fullWidth
                  value={newSlipNumber}
                  onChange={(e) => setNewSlipNumber(e.target.value)}
                  error={!!newSlipError}
                  helperText={newSlipError}
                  variant="outlined"
                  sx={{ mt: 2 }}
                />
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setAddSlipDialogOpen(false)}>Cancel</Button>
                <Button onClick={handleCreateSlip} variant="contained" color="primary">
                  Create Slip
                </Button>
              </DialogActions>
            </Dialog>

            {/* Re-Assign Vessel Dialog */}
            <Dialog
              open={reassignDialogOpen}
              onClose={() => {
                setReassignDialogOpen(false);
                setReassignFromSlipId('');
                setReassignToSlipId('');
                setReassignError(null);
                setReassignSuccess(null);
              }}
              maxWidth="sm"
              fullWidth
            >
              <DialogTitle>Re-Assign Launched Vessel to Different Slip</DialogTitle>
              <DialogContent dividers>
                {reassignError && <Alert severity="error" sx={{ mb: 2 }}>{reassignError}</Alert>}
                {reassignSuccess && <Alert severity="success" sx={{ mb: 2 }}>{reassignSuccess}</Alert>}
                <Box mb={2}>
                  <FormControl fullWidth>
                    <InputLabel>Slip with Launched Vessel</InputLabel>
                    <Select
                      value={reassignFromSlipId}
                      label="Slip with Launched Vessel"
                      onChange={e => {
                        setReassignFromSlipId(e.target.value);
                        setReassignToSlipId('');
                        setReassignError(null);
                        setReassignSuccess(null);
                      }}
                    >
                      {slips
                        .filter(slip => slip.assignedVesselId && slip.launchStatus === 'launched')
                        .sort((a, b) => (a.slipNumber || '').localeCompare(b.slipNumber || ''))
                        .map(slip => {
                          // Find customer name for this slip
                          let customerName = '';
                          if (slip.customerId) {
                            const customer = slips.find(s => s.id === slip.id)?.renterName || '';
                            customerName = customer;
                          }
                          return (
                            <MenuItem key={slip.id} value={slip.id}>
                              {slip.slipNumber || slip.id} {customerName ? `(Customer: ${customerName})` : ''}
                            </MenuItem>
                          );
                        })}
                    </Select>
                  </FormControl>
                </Box>
                <Box mb={2}>
                  <FormControl fullWidth>
                    <InputLabel>New Slip (Available/Unassigned)</InputLabel>
                    <Select
                      value={reassignToSlipId}
                      label="New Slip (Available/Unassigned)"
                      onChange={e => {
                        setReassignToSlipId(e.target.value);
                        setReassignError(null);
                        setReassignSuccess(null);
                      }}
                      disabled={!reassignFromSlipId}
                    >
                      {slips
                        .filter(slip => !slip.assignedVesselId)
                        .sort((a, b) => (a.slipNumber || a.id).localeCompare(b.slipNumber || b.id))
                        .map(slip => (
                          <MenuItem key={slip.id} value={slip.id}>
                            {slip.slipNumber ? slip.slipNumber : `(ID: ${slip.id})`}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setReassignDialogOpen(false)} disabled={reassignLoading}>Cancel</Button>
                <Button
                  variant="contained"
                  color="primary"
                  disabled={!reassignFromSlipId || !reassignToSlipId || reassignLoading}
                  onClick={async () => {
                    setReassignLoading(true);
                    setReassignError(null);
                    setReassignSuccess(null);
                    try {
                      const fromSlip = slips.find(s => s.id === reassignFromSlipId);
                      const toSlip = slips.find(s => s.id === reassignToSlipId);
                      if (!fromSlip || !toSlip || !fromSlip.assignedVesselId) {
                        setReassignError('Invalid slip selection.');
                        setReassignLoading(false);
                        return;
                      }
                      // Remove vessel from old slip
                      const updatedFromSlip = { ...fromSlip, assignedVesselId: null, launchStatus: 'not_launched' as const };
                      // Assign vessel to new slip
                      const updatedToSlip = { ...toSlip, assignedVesselId: fromSlip.assignedVesselId, launchStatus: 'launched' as const };
                      await upsertSlip(updatedFromSlip);
                      await upsertSlip(updatedToSlip);
                      setReassignSuccess('Vessel reassigned successfully!');
                      // Update UI
                      setSlips(prev => prev.map(slip =>
                        slip.id === updatedFromSlip.id ? updatedFromSlip :
                        slip.id === updatedToSlip.id ? updatedToSlip : slip
                      ));
                      setReassignFromSlipId('');
                      setReassignToSlipId('');
                    } catch (err: any) {
                      setReassignError('Failed to reassign vessel. ' + (err?.message || ''));
                    } finally {
                      setReassignLoading(false);
                    }
                  }}
                >
                  {reassignLoading ? <CircularProgress size={24} /> : 'Re-Assign'}
                </Button>
              </DialogActions>
            </Dialog>

            {/* Location Snackbar */}
            <Snackbar
              open={showLocationSnackbar}
              autoHideDuration={6000}
              onClose={() => setShowLocationSnackbar(false)}
              message={locationSnackbarMessage}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            />
          </Box>
        </Box>
      </FullScreenLayout>
    </ThemeProvider>
  );
};

export default SlipManagementPage; 